import React, { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { useQuery } from "@apollo/client"
import { FaChevronRight } from "react-icons/fa"
import { DataLoading, Theme } from "@pga/pga-component-library"

import CurriculumTracker from "../CurriculumTracker"
import { ButtonFw } from "../../Button"
import { urlFor, getCurrentProgressLevelCode } from "./CurriculumMessage/utils"
import CurriculumMessage from "./CurriculumMessage"
import {
  MembershipJourneyProvider,
  useMembershipJourneyContext,
} from "../MembershipJourney/MembershipJourneyContext"
import {
  getCurrentLevelReapplyStatus,
  resolveAcceptableProgressStanding,
} from "../../../utils/resolveAcceptableProgressStanding"
import {
  shouldShowCurriculumReapplyCta,
  shouldSuppressAdvancementCtas,
  shouldUseTerminatedGptpCopy,
} from "../../../utils/shouldSuppressAdvancementCtas"
import { GPTP_MESSAGE_VARIANT } from "../MembershipJourney/utils/suspensionUtils"
import { flags, useFeatureFlags } from "../../../lib/feature-flags"
import { isAffiliate, isStudent } from "../../utils"
import { COACH_TOOLS_API } from "../../../apolloClient"
import {
  MEMBER_APPLICATIONS_URL,
  redirectToMemberApplications,
} from "../../../lib/ptmApplicationsRedirect"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../../queries/associateApplicationEligibility"
import { PTM_MEMBERSHIP_APPLICATIONS_QUERY } from "../../../utils/prospect/withPtmApplicationsQuery"
import {
  pickLatestApplicationForType,
  ptmApplyCtaLabel,
  PTM_APPLICATION_TYPES,
} from "../../../utils/prospect/ptmApplicationStatus"
import {
  areReapplyPathApplicationsReady,
  reapplyPathActionState,
} from "../../../utils/reapplyPathOffer"
import { shouldShowAffiliateAssociateApplicationLink } from "../../../utils/affiliateAssociateApplicationLink"
import PtmApplicationStatus from "../../PtmApplicationStatus"
import BecomeAssociateModal from "./BecomeAssociateModal"
import AdditionalRequirementsModal from "./AdditionalRequirementsModal"
import {
  formatAdditionalRequirementsButtonLabel,
  getAdditionalRequirementsCompletionSummary,
} from "../MembershipJourney/AdditionalRequirementsList"
import { buildAdditionalRequirementItems } from "../MembershipJourney/utils/additionalRequirementItems"
import { shouldShowCourseCurriculumAdditionalRequirements } from "../MembershipJourney/utils/shouldShowCourseCurriculumAdditionalRequirements"
import { STATUS } from "../MembershipJourney/utils/constants/statuses"

const Box = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 20px 0 20px;
  width: 100%;
  border-right: 2px solid ${Theme.colors.darkgrey};

  @media (max-width: 767px) {
    padding-top: 35px;
  }
`

export const ButtonsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`

// Warning treatment matches the incomplete-card border in
// MembershipJourney/Styles/ProgressTracker.styles.js.
const AdditionalRequirementsButton = styled(ButtonFw)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props =>
    props.$hasWarning &&
    `
    border: solid 2px #DC994A;
    color: #DC994A;
  `}
`

const LevelTrackBox = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
`

export const TextWrapper = styled.div`
  padding: 0 15px;
`

export const Loading = () => (
  <Box>
    <br />
    <DataLoading h={25} />
    <br />
    <DataLoading h={60} />
    <ButtonsBox>
      <DataLoading h={40} w={150} />
      <DataLoading h={40} w={150} />
    </ButtonsBox>
  </Box>
)

const CourseCurriculum = () => {
  const [isBecomeAssociateModalOpen, setIsBecomeAssociateModalOpen] =
    useState(false)
  const [
    isAdditionalRequirementsModalOpen,
    setIsAdditionalRequirementsModalOpen,
  ] = useState(false)
  const parentContext = useMembershipJourneyContext()
  const {
    me,
    pgmProgress,
    memberApplicationEligibility,
    memberApplicationEligibilityLoading = false,
    isMemberApplicationChecklistEnabled = false,
    pgmGraduate = false,
  } = parentContext
  const affiliateViewer = isAffiliate(me)
  const [isTerminatedNoticeEnabled = false] =
    useFeatureFlags([flags.FLAG_FEAT_TERMINATED_NOTICE]) ?? []
  const progressStanding = resolveAcceptableProgressStanding({
    me,
    pgmProgress,
  })
  const aptContext = {
    ...parentContext,
    progressStanding,
    gptpMessageVariant: shouldUseTerminatedGptpCopy({
      isTerminatedNoticeEnabled,
      standing: progressStanding,
    })
      ? GPTP_MESSAGE_VARIANT.TERMINATED
      : GPTP_MESSAGE_VARIANT.SUSPENDED,
  }
  const suppressLegacyBecomeAssociate = shouldSuppressAdvancementCtas({
    standing: progressStanding,
    isTerminatedNoticeEnabled,
  })

  const skipAssociateCtaQueries =
    !affiliateViewer || suppressLegacyBecomeAssociate
  const { data: eligibilityData, loading: eligibilityLoading } = useQuery(
    ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY,
    {
      context: { api: COACH_TOOLS_API },
      errorPolicy: "all",
      fetchPolicy: "network-only",
      skip: skipAssociateCtaQueries,
    },
  )
  const {
    data: ptmApplicationsData,
    loading: ptmApplicationsLoading,
    error: ptmApplicationsError,
  } = useQuery(PTM_MEMBERSHIP_APPLICATIONS_QUERY, {
    context: { api: COACH_TOOLS_API },
    skip: skipAssociateCtaQueries,
  })

  const associateCtaQueriesReady =
    skipAssociateCtaQueries ||
    (!eligibilityLoading &&
      areReapplyPathApplicationsReady({
        shouldFetch: true,
        loading: ptmApplicationsLoading,
        data: ptmApplicationsData,
        error: ptmApplicationsError,
      }))
  const membershipApplications = ptmApplicationsError
    ? []
    : ptmApplicationsData?.membershipApplications || []
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const associateEligible =
    eligibilityData?.associateApplicationEligibility?.eligible === true
  const associateAction = reapplyPathActionState(
    associateApplication,
    shouldShowAffiliateAssociateApplicationLink({
      pgmProgress,
      eligibilityEligible: associateEligible,
      standing: progressStanding,
    }),
    eligibilityData?.associateApplicationEligibility?.pathEligible,
  )
  const showBecomeAssociateButton =
    !skipAssociateCtaQueries &&
    associateCtaQueriesReady &&
    associateAction.showApplyCta &&
    !associateAction.isResume
  const showResumeAssociateButton =
    !skipAssociateCtaQueries &&
    associateCtaQueriesReady &&
    associateAction.showApplyCta &&
    associateAction.isResume
  const showAssociateApplicationStatus =
    !skipAssociateCtaQueries &&
    associateCtaQueriesReady &&
    associateAction.showStatus
  const showReapplyButton = shouldShowCurriculumReapplyCta({
    isTerminatedNoticeEnabled,
    standing: progressStanding,
    reapplyStatus: getCurrentLevelReapplyStatus(pgmProgress),
  })

  useEffect(() => {
    if (!showBecomeAssociateButton) {
      setIsBecomeAssociateModalOpen(false)
    }
  }, [showBecomeAssociateButton])

  const current = getCurrentProgressLevelCode(me)
  const transcriptUrl = urlFor("transcript", me)
  const portalUrl = urlFor("portal", me)
  const shouldShowAdditionalRequirements =
    shouldShowCourseCurriculumAdditionalRequirements({
      me,
      pgmGraduate,
      isMemberApplicationChecklistEnabled,
    })
  const additionalRequirementItems = useMemo(() => {
    if (!shouldShowAdditionalRequirements) {
      return []
    }

    return buildAdditionalRequirementItems({
      eligibility: isMemberApplicationChecklistEnabled
        ? memberApplicationEligibility
        : undefined,
      showPatWarning: Boolean(
        pgmProgress?.patStatus && pgmProgress.patStatus !== STATUS.COMPLETE,
      ),
      suspensions: pgmProgress?.suspensions || [],
      isMemberApplicationChecklistEnabled,
      isPgmGraduate: pgmGraduate === true,
      useStudentStyleChecklist: pgmGraduate === true || isStudent(me),
    })
  }, [
    isMemberApplicationChecklistEnabled,
    me,
    memberApplicationEligibility,
    pgmGraduate,
    pgmProgress?.patStatus,
    pgmProgress?.suspensions,
    shouldShowAdditionalRequirements,
  ])
  const additionalRequirementsSummary = useMemo(
    () =>
      getAdditionalRequirementsCompletionSummary(additionalRequirementItems),
    [additionalRequirementItems],
  )
  const additionalRequirementsButtonLabel =
    formatAdditionalRequirementsButtonLabel(additionalRequirementsSummary)
  const hasIncompleteAdditionalRequirements =
    additionalRequirementsSummary != null &&
    additionalRequirementsSummary.completeCount <
      additionalRequirementsSummary.totalCount

  return (
    <MembershipJourneyProvider value={aptContext}>
      <Box>
        <TextWrapper>{CurriculumMessage(me)}</TextWrapper>
        <LevelTrackBox>
          <CurriculumTracker current={current} me={me} />
        </LevelTrackBox>
        <ButtonsBox>
          {shouldShowAdditionalRequirements &&
            !memberApplicationEligibilityLoading &&
            additionalRequirementItems.length > 0 && (
              <div className="w-100 p-3">
                <AdditionalRequirementsButton
                  outline
                  $hasWarning={hasIncompleteAdditionalRequirements}
                  aria-haspopup="dialog"
                  aria-expanded={isAdditionalRequirementsModalOpen}
                  onClick={() => setIsAdditionalRequirementsModalOpen(true)}
                >
                  {additionalRequirementsButtonLabel}
                  <FaChevronRight size={12} aria-hidden="true" />
                </AdditionalRequirementsButton>
              </div>
            )}
          {showReapplyButton && (
            <a
              className="w-100 p-3"
              href={MEMBER_APPLICATIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonFw>Reapply</ButtonFw>
            </a>
          )}
          {showAssociateApplicationStatus && (
            <div className="w-100 p-3">
              <PtmApplicationStatus
                status={associateApplication.status}
                registrationStatus={associateApplication.registrationStatus}
                date={
                  associateAction.showDeniedStatus
                    ? associateApplication.updatedAt
                    : undefined
                }
                applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
              />
            </div>
          )}
          {(showBecomeAssociateButton || showResumeAssociateButton) && (
            <div className="w-100 p-3">
              <ButtonFw
                onClick={
                  showResumeAssociateButton
                    ? redirectToMemberApplications
                    : () => setIsBecomeAssociateModalOpen(true)
                }
              >
                {ptmApplyCtaLabel({
                  applicationType: PTM_APPLICATION_TYPES.ASSOCIATE,
                  isResume: associateAction.isResume,
                  isClosedRegistration: associateAction.isClosedRegistration,
                })}
              </ButtonFw>
            </div>
          )}
          {transcriptUrl && (
            <a
              className="w-100 p-3"
              href={transcriptUrl}
              rel="noopener noreferrer"
            >
              <ButtonFw outline>VIEW TRANSCRIPT</ButtonFw>
            </a>
          )}
          {portalUrl && (
            <a
              className="w-100 p-3"
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ButtonFw outline={showReapplyButton}>EDUCATION PORTAL</ButtonFw>
            </a>
          )}
        </ButtonsBox>
        <BecomeAssociateModal
          isOpen={isBecomeAssociateModalOpen}
          onClose={() => setIsBecomeAssociateModalOpen(false)}
          onApply={redirectToMemberApplications}
        />
        <AdditionalRequirementsModal
          isOpen={isAdditionalRequirementsModalOpen}
          onClose={() => setIsAdditionalRequirementsModalOpen(false)}
          items={additionalRequirementItems}
        />
      </Box>
    </MembershipJourneyProvider>
  )
}

export default CourseCurriculum
